// docs/60 DC14 — the dungeon crawl as the HUD and the board read it: where a hero is (the chamber
// and the depth), how far the party has come, which lair a log entry is about, and the words the
// banners and call-outs show for each crawl effect. Pure functions over `Snapshot.crawl` (the
// party's view: only awake lairs, unseen chambers unnamed, unread lore blank).
import { wakeHead } from '../snares/snares';
import type { BoonName, CrawlArea, CrawlDepth, CrawlLair, CrawlView, Effect, FixtureView, Tile } from '../sim/types';

/** The chamber index on square `t` of a board `w` wide, if the party knows one there. */
export function areaAt(crawl: CrawlView, w: number, t: Tile): number | undefined {
  if (t.x < 0 || t.y < 0 || t.x >= w) return undefined;
  const a = crawl.areaOf[t.y * w + t.x] ?? 0;
  return a > 0 && a - 1 < crawl.areas.length ? a - 1 : undefined;
}

/** The depth whose rectangle holds `t` (depths are laid side by side, so at most one does). */
export function depthAt(crawl: CrawlView, t: Tile): number | undefined {
  const i = crawl.depths.findIndex((d) => t.x >= d.x0 && t.x <= d.x1 && t.y >= d.y0 && t.y <= d.y1);
  return i < 0 ? undefined : i;
}

export interface Whereabouts {
  area?: { index: number } & CrawlArea;
  depth?: { index: number } & CrawlDepth;
}

/** Where a hero standing on `t` is: its chamber (when known) and its depth (by the chamber, else the rectangle). */
export function whereIs(crawl: CrawlView, w: number, t: Tile): Whereabouts {
  const out: Whereabouts = {};
  const a = areaAt(crawl, w, t);
  if (a !== undefined) out.area = { index: a, ...crawl.areas[a]! };
  const d = out.area && out.area.depth < crawl.depths.length ? out.area.depth : depthAt(crawl, t);
  if (d !== undefined) out.depth = { index: d, ...crawl.depths[d]! };
  return out;
}

/** "the Flooded Undercroft" or, before the party has set foot there, "Depth 2". */
export function depthName(crawl: CrawlView, i: number): string {
  const d = crawl.depths[i];
  return d?.name || `Depth ${i + 1}`;
}

/** How many depths the party has set foot on, of how many there are (the pit cell is aside, not a depth). */
export function depthsReached(crawl: CrawlView): { entered: number; total: number } {
  const real = crawl.depths.filter((d) => !d.aside);
  return { entered: real.filter((d) => d.entered).length, total: real.length };
}

/** Depth `i`'s place among the crawl's real depths (1-based), or undefined for a place aside (the pit cell). */
export function depthNumber(crawl: CrawlView, i: number): number | undefined {
  if (crawl.depths[i]?.aside) return undefined;
  return crawl.depths.slice(0, i + 1).filter((d) => !d.aside).length;
}

/** How many chambers the party has entered (of the ones it knows of, and of all). */
export function chambersEntered(crawl: CrawlView): { entered: number; total: number } {
  return { entered: crawl.areas.filter((a) => a.entered).length, total: crawl.areas.length };
}

export function lairsWoken(crawl: CrawlView): number { return crawl.lairs.filter((l) => l.awake).length; }

/**
 * The lair a `LairWoke` entry is about. The entry's `lair` indexes the truth's list, but the party's
 * view keeps only awake lairs, so trust the index only when that lair shares a unit with the entry
 * (or the entry names none and the lists are whole); otherwise match by the units it names.
 */
export function lairFor(crawl: CrawlView | undefined, e: Extract<Effect, { kind: 'LairWoke' }>): CrawlLair | undefined {
  if (!crawl) return undefined;
  const shares = (l: CrawlLair) => l.units.some((u) => e.units.includes(u));
  const byIndex = crawl.lairs[e.lair];
  if (byIndex && (shares(byIndex) || (!e.units.length && !byIndex.units.length))) return byIndex;
  return crawl.lairs.find(shares);
}

/** docs/60 DC10: what a shrine does, as the board and the call-out say it. */
export const BOON_WORDS: Record<BoonName, { verb: string; line: string; colour: number }> = {
  Heal: { verb: 'heals', line: 'Wounds close and every harm is lifted.', colour: 0x6fe08a },
  Bless: { verb: 'blesses', line: 'A warmth settles on you: your blows land truer for a while.', colour: 0xffd166 },
  Ward: { verb: 'wards', line: 'A pale ward wraps you: harm glances off for a while.', colour: 0x8fd3ff },
  Reveal: { verb: 'reveals what is hidden', line: 'The depth gives up its secrets: hidden ways and traps show themselves.', colour: 0xb48cff },
};

/** "Shrine of the Pilgrims' Altar — blesses (2 uses left)" / "… — spent". */
export function shrineLabel(f: FixtureView): string {
  const name = f.name.replace(/^the /i, '') || 'shrine';
  const boon = f.boon ? BOON_WORDS[f.boon].verb : 'answers';
  const uses = f.uses ?? 0;
  return `Shrine of ${name} — ${uses > 0 ? `${boon} (${uses} use${uses === 1 ? '' : 's'} left)` : 'spent'}`;
}

/** "Stair down" / "Stair up". */
export function stairLabel(f: FixtureView): string { return f.down ? 'Stair down' : 'Stair up'; }

/** docs/60 DC7: a light's look (`LightLook` order) and how the board and the light map draw it. */
export interface LightLookDef { key: 'sconce' | 'brazier' | 'candles' | 'moss' | 'shrine' | 'ember'; name: string; colour: [number, number, number]; intensity: number; flicker: 'torch' | 'fire' | 'crystal' | 'lava'; glow: number }
export const LIGHT_LOOKS: readonly LightLookDef[] = [
  { key: 'sconce', name: 'a sconce', colour: [1.0, 0.62, 0.3], intensity: 0.66, flicker: 'torch', glow: 0.35 },
  { key: 'brazier', name: 'a brazier', colour: [1.0, 0.52, 0.22], intensity: 0.74, flicker: 'fire', glow: 0.4 },
  { key: 'candles', name: 'candles', colour: [1.0, 0.8, 0.5], intensity: 0.55, flicker: 'torch', glow: 0.3 },
  { key: 'moss', name: 'glowing moss', colour: [0.45, 1.0, 0.75], intensity: 0.42, flicker: 'crystal', glow: 0.3 },
  { key: 'shrine', name: 'a shrine’s glow', colour: [0.75, 0.85, 1.0], intensity: 0.6, flicker: 'crystal', glow: 0.35 },
  { key: 'ember', name: 'an ember crack', colour: [1.0, 0.42, 0.15], intensity: 0.5, flicker: 'lava', glow: 0.3 },
];
export const lightLook = (look: number): LightLookDef => LIGHT_LOOKS[look] ?? LIGHT_LOOKS[0]!;

/** How a crawl moment is shown: a big depth banner, a chamber banner, or a call-out over the board. */
export type CalloutKind = 'depth' | 'chamber' | 'lair' | 'roamer' | 'lore' | 'shrine' | 'fall' | 'ending' | 'treasure';
export interface Callout { kind: CalloutKind; head: string; line: string; eyebrow?: string; tile?: Tile; unit?: number }

const WOKE_WHY: Record<string, string> = {
  Seen: 'saw you', Entered: 'heard you come in', DoorOpened: 'heard the door', Disturbed: 'felt their hoard disturbed',
  Hurt: 'felt the blow', Alarm: 'heard the alarm', Heard: 'heard you in the dark',
};

/**
 * The banner or call-out a crawl entry makes (undefined for any other entry, or one with nothing to
 * say). `crawl` should be the board after the pulse when there is one: the party's view names a
 * chamber, a depth and a lore fragment only once the party has been there or read it.
 */
export function crawlCallout(e: Effect, crawl: CrawlView | undefined, name: (unit: number) => string | undefined): Callout | undefined {
  const who = (u: number | undefined) => (u !== undefined ? name(u) : undefined);
  switch (e.kind) {
    case 'AreaEntered': {
      if (e.depth !== undefined) {
        const d = crawl?.depths[e.depth];
        const n = crawl ? depthNumber(crawl, e.depth) : e.depth + 1;
        const eyebrow = n === undefined ? 'Fallen below' : `Depth ${n}${crawl ? ` of ${depthsReached(crawl).total}` : ''}`;
        return { kind: 'depth', head: d?.name || `Depth ${n ?? e.depth + 1}`, line: d?.text ?? '', eyebrow, unit: e.unit };
      }
      const a = crawl?.areas[e.area];
      if (!a?.name) return undefined;
      return { kind: 'chamber', head: a.name, line: a.line, unit: e.unit };
    }
    case 'LairWoke': {
      const l = lairFor(crawl, e);
      const why = WOKE_WHY[e.why];
      // docs/140 DT13: the dweller's own lair is named by its title — "The Drowned Abbot wakes"
      const d = crawl?.dweller;
      const single = d !== undefined && (!!l?.units.includes(d) || e.units.includes(d));
      const head = l?.name ? wakeHead(l.name, single) : 'Something wakes in the dark';
      return { kind: 'lair', head, line: l?.line ?? '', ...(why ? { eyebrow: `They ${why}` } : {}), ...(e.units[0] !== undefined ? { unit: e.units[0] } : {}) };
    }
    case 'Roused': {
      const r = crawl?.roamers.find((x) => x.unit === e.unit);
      const foe = who(e.unit);
      const by = who(e.by);
      return { kind: 'roamer', head: `${foe ? cap(foe) : 'Something'} has found ${by ?? 'you'}`, line: r?.line ?? '', eyebrow: 'A prowler', unit: e.unit };
    }
    case 'LoreRead': {
      const l = crawl?.lore[e.lore];
      return { kind: 'lore', head: l?.title ? cap(l.title) : 'Lore read', line: l?.text ?? '', eyebrow: 'Added to the journal', unit: e.unit };
    }
    case 'ShrineAnswered': {
      const w = BOON_WORDS[e.boon];
      const by = who(e.unit);
      // docs/140 DT13: the storyteller's words for the shrine, when it wrote some
      return { kind: 'shrine', head: `The shrine ${w?.verb ?? 'answers'}${by ? ` ${by}` : ''}`, line: crawl?.shrine || w?.line || '', unit: e.unit };
    }
    case 'Fell': {
      const by = who(e.unit);
      return { kind: 'fall', head: `${by ?? 'A hero'} fell through a chute!`, line: 'Down into a sealed cell — there is always a way out.', tile: e.to, unit: e.unit };
    }
    default: return undefined;
  }
}

/** True for the effects the crawl adds to the log (docs/60). */
export function isCrawlEffect(e: Effect): e is Extract<Effect, { kind: 'AreaEntered' | 'LairWoke' | 'Roused' | 'LoreRead' | 'ShrineAnswered' | 'Fell' }> {
  return e.kind === 'AreaEntered' || e.kind === 'LairWoke' || e.kind === 'Roused' || e.kind === 'LoreRead' || e.kind === 'ShrineAnswered' || e.kind === 'Fell';
}

/**
 * docs/60 DC14 camera: where to open on a crawl — the heroes' centroid and the zoom that shows a
 * comfortable square of board round them (`span` squares across the shorter side of the view),
 * never zoomed out past a fit of the heroes themselves.
 */
export function partyFrame(heroes: readonly Tile[], span = 16): { x: number; y: number; span: number } | undefined {
  if (!heroes.length) return undefined;
  const xs = heroes.map((t) => t.x), ys = heroes.map((t) => t.y);
  const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
  return { x: (x0 + x1) / 2 + 0.5, y: (y0 + y1) / 2 + 0.5, span: Math.max(span, x1 - x0 + 6, y1 - y0 + 6) };
}

/** The pulses a fight may run before it is a stalemate: a crawl's own limit, else the sim's. */
export function pulseLimit(crawl: CrawlView | undefined, fallback: number): number {
  return crawl?.pulseLimit || fallback;
}

const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

/** docs/118 PT5: XP each hero's crawl reward falls by for every door forced (server `crawl::FORCE_XP`). */
export const FORCE_XP = 15;

/** docs/118 PT5: a shut door or gate one of `heroIds` stands beside: who can force what. */
export interface Forceable { heroId: number; hero: string; fixture: number; door: string }

/**
 * docs/118 PT5: the shut doors and gates of a crawl that one of `heroIds` (up, on the board) stands
 * beside (a side of its body touching the door's square, as `fk_sim::crawl::can_force` asks), the
 * `prefer`red hero's first. Pending doors (forced, giving way this turn) are left out.
 */
export function forceableDoors(snap: { crawl?: CrawlView; units: readonly { heroId?: number; name: string; tile: Tile; size?: number; hp: number; onBoard?: boolean }[]; fixtures?: readonly FixtureView[] }, heroIds: readonly number[], prefer?: number, pending: readonly number[] = []): Forceable[] {
  if (!snap.crawl) return [];
  const shut = (snap.fixtures ?? []).filter((f) => (f.kind === 'Door' || f.kind === 'Gate') && !f.on && !pending.includes(f.id));
  const heroes = snap.units
    .filter((u) => u.heroId !== undefined && heroIds.includes(u.heroId) && u.onBoard !== false && u.hp > 0)
    .sort((a, b) => Number(b.heroId === prefer) - Number(a.heroId === prefer));
  const out: Forceable[] = [];
  for (const f of shut) {
    const h = heroes.find((u) => touches(u.tile, u.size ?? 1, f.tile));
    if (h && h.heroId !== undefined) out.push({ heroId: h.heroId, hero: h.name, fixture: f.id, door: f.name });
  }
  return out.sort((a, b) => Number(b.heroId === prefer) - Number(a.heroId === prefer));
}

/** Whether a body of `size` at `origin` has a side against `t` (diagonals do not touch, docs/39 FW2). */
function touches(origin: Tile, size: number, t: Tile): boolean {
  const s = Math.max(1, size);
  const inside = t.x >= origin.x && t.x < origin.x + s && t.y >= origin.y && t.y < origin.y + s;
  if (inside) return false;
  const dx = t.x < origin.x ? origin.x - t.x : t.x >= origin.x + s ? t.x - (origin.x + s - 1) : 0;
  const dy = t.y < origin.y ? origin.y - t.y : t.y >= origin.y + s ? t.y - (origin.y + s - 1) : 0;
  return dx + dy === 1;
}

/**
 * docs/140 DT13: the crawl's own moments that are not crawl entries — the ending the storyteller
 * wrote, read when the dweller falls, and the treasure's words when its relic is taken up. `crawl`
 * is the board after the pulse (the party's view unlocks the ending only once the dweller is down).
 */
export function storyCallout(e: Effect, crawl: CrawlView | undefined, fixture: (id: number) => FixtureView | undefined): Callout | undefined {
  if (!crawl) return undefined;
  if (e.kind === 'Dropped' && crawl.dweller !== undefined && e.unit === crawl.dweller && crawl.ending) {
    return { kind: 'ending', head: `${crawl.name ? cap(crawl.name) : 'The crawl'} is won`, line: crawl.ending, eyebrow: 'The dweller falls', unit: e.unit };
  }
  if (e.kind === 'Interacted' && e.taken && crawl.treasure) {
    const f = fixture(e.fixture);
    if (f?.kind === 'Relic') return { kind: 'treasure', head: f.name ? cap(f.name) : 'The treasure', line: crawl.treasure, eyebrow: 'Treasure found' };
  }
  return undefined;
}

/**
 * docs/140 DT13: the crawl's briefing, as the card at its start says it — its name, the quest it is
 * a stop of, what happened here, why the party came, and what wins it.
 */
export interface Briefing { name: string; quest?: string; premise?: string; hook?: string; goal: string; depth?: { name: string; text: string } }

export function briefingOf(crawl: CrawlView, dweller: string | undefined): Briefing {
  const out: Briefing = { name: crawl.name ? cap(crawl.name) : 'The crawl', goal: dweller ? `Find and defeat ${dweller}, deep in the dark.` : 'Find what dwells at the bottom of the dark, and defeat it.' };
  if (crawl.quest) out.quest = crawl.quest;
  if (crawl.premise) out.premise = crawl.premise;
  if (crawl.hook) out.hook = crawl.hook;
  const d = crawl.depths.find((x) => !x.aside && x.entered) ?? crawl.depths[0];
  if (d?.name) out.depth = { name: d.name, text: d.text };
  return out;
}
